export type Dictionary = Record<string, string>

export type DictionaryCacheEntry = {
  dictionary: Dictionary
  complete: boolean
}

type DictionaryLoader = () => Promise<Dictionary>

export class DictionaryCache<Locale extends string = string> {
  private readonly entries = new Map<Locale, DictionaryCacheEntry>()
  private readonly pendingLoads = new Map<Locale, Promise<Dictionary>>()
  private readonly listeners = new Set<() => void>()
  private version = 0

  get = (locale: Locale): DictionaryCacheEntry | undefined =>
    this.entries.get(locale)

  getVersion = (): number => this.version

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  set(locale: Locale, dictionary: Dictionary, complete: boolean): void {
    const cached = this.entries.get(locale)
    if (cached?.complete && !complete) return
    if (cached?.dictionary === dictionary && cached.complete === complete) {
      return
    }

    this.entries.set(locale, { dictionary, complete })
    this.version += 1
    this.listeners.forEach((listener) => listener())
  }

  loadComplete(locale: Locale, loader: DictionaryLoader): Promise<Dictionary> {
    const cached = this.entries.get(locale)
    if (cached?.complete) return Promise.resolve(cached.dictionary)

    const pending = this.pendingLoads.get(locale)
    if (pending) return pending

    const promise = loader()
      .then((dictionary) => {
        this.set(locale, dictionary, true)
        return dictionary
      })
      .finally(() => {
        this.pendingLoads.delete(locale)
      })
    this.pendingLoads.set(locale, promise)
    return promise
  }
}
